import 'mapbox-gl/dist/mapbox-gl.css'
import React, { useState, useRef, useEffect } from 'react'
import mapboxgl from 'mapbox-gl'
import ReactDOM from 'react-dom'
import styles from './Map.module.scss'

/**
 * Renders a single event card.
 * This is not a standard Next component since Mapbox doesn't support server-side rendering.
 */
const EventCard = ({ event }) => {
  const cardStyles = {
    cardContainer: {
      width: '100%',
      fontFamily: ['Poppins', 'sans-serif'],
      boxShadow: '0 4px 50px 10px rgba(0,0,0,.1)',
    },
    imageContainer: {
      width: '100%',
      position: 'relative',
      paddingBottom: '66.66%',
    },
    cardImage: {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      objectPosition: 'center',
      borderRadius: '10px 10px 0 0',
    },
    cardContent: {
      padding: '1rem',
    },
    cardTitle: {
      marginBottom: '10px',
      fontWeight: 'bold',
      fontSize: '18px',
      overflowWrap: 'break-word',
    },
    cardDescription: {
      marginBottom: '8px',
      lineHeight: '1.5',
      fontSize: '14px',
    },
    flexContainer: {
      display: 'flex',
      alignItems: 'center',
      marginBottom: '7px',
    },
    userIcon: {
      width: '20px',
      height: '20px',
      marginRight: '5px',
      backgroundImage: `url('img/user-pink.svg')`,
      backgroundSize: '20px',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
    },
    priceIcon: {
      width: '20px',
      height: '20px',
      marginRight: '5px',
      backgroundImage: `url('img/price.svg')`,
      backgroundSize: '20px',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
    },
    boldPinkText: {
      color: '#FF658B',
      fontWeight: 'bold',
      fontSize: '14px',
    },
    ctaButton: {
      backgroundColor: '#FF658B',
      color: 'white',
      borderRadius: '9999px',
      width: '100%',
      padding: '0.75rem',
      marginTop: '10px',
      marginBottom: '6px',
      transition: 'all 0.3s',
      cursor: 'pointer',
      borderStyle: 'none',
      fontWeight: '400',
      fontFamily: ['Poppins', 'sans-serif'],
      fontSize: '16px',
      display: 'inline-block',
      textAlign: 'center',
      textDecoration: 'none',
    },
  }

  return (
    <div style={cardStyles.cardContainer}>
      <div className={cardStyles.imageContainer}>
        <img
          src={event.image.sizes.teaser}
          style={cardStyles.cardImage}
          alt={`Kuva tapahtumasta nimeltä ${event.name}`}
        />
      </div>
      <div style={cardStyles.cardContent}>
        <h4 style={cardStyles.cardTitle}>{event.name}</h4>
        <p style={cardStyles.cardDescription}>{event.description}</p>
        <div style={cardStyles.flexContainer}>
          <div style={cardStyles.userIcon} />
          <p style={cardStyles.boldPinkText}>{event.guests}</p>
        </div>
        <div style={cardStyles.flexContainer}>
          <div style={cardStyles.priceIcon} />
          <p style={cardStyles.boldPinkText}>{event.price}</p>
        </div>
        <a
          style={cardStyles.ctaButton}
          href={`/tapahtuma/${event.slug}`}
          target="_blank"
          rel="noreferrer"
        >
          Lue lisää
        </a>
      </div>
    </div>
  )
}

const MapboxMap = ({ events, center, zoom }) => {
  const [map, setMap] = useState(null)
  const mapContainer = useRef(null)
  const [displayZoomInfo, setDisplayZoomInfo] = useState(false)
  const [selectedId, setSelectedId] = useState(null)

  /*
   * Extracts the latitude and longitude from a Google Maps link
   */
  const extractLngLatFromGMapsLink = (link) => {
    if (!link) return null
    const pattern = /([+-]?\d*\.?\d+),([+-]?\d*\.?\d+)/
    const lngLat = link.match(pattern)
    if (!lngLat) return null
    return {
      lng: Number(lngLat[2]),
      lat: Number(lngLat[1]),
    }
  }

  /*
   * Adds the layers and sources to the map
   */
  const addLayersAndSources = (eventDataSource) => {
    map.addSource('fetchedEvents', {
      type: 'geojson',
      data: eventDataSource,
      cluster: true, // Enable clustering
      clusterMaxZoom: 10, // Max zoom to cluster points
      clusterRadius: 50, // Radius of each cluster
    })

    map.addLayer({
      id: 'clusters',
      type: 'circle',
      source: 'fetchedEvents',
      filter: ['has', 'point_count'],
      paint: {
        'circle-color': '#FF658B',
        'circle-radius': ['step', ['get', 'point_count'], 20, 100, 30, 500, 40],
      },
    })

    map.addLayer({
      id: 'cluster-count',
      type: 'symbol',
      source: 'fetchedEvents',
      filter: ['has', 'point_count'],
      layout: {
        'text-field': '{point_count_abbreviated}',
        'text-font': ['DIN Offc Pro Medium', 'Arial Unicode MS Bold'],
        'text-size': 12,
      },
      paint: {
        'text-color': '#fff',
      },
    })

    map.addLayer({
      id: 'unclustered-point',
      type: 'symbol',
      source: 'fetchedEvents',
      filter: ['!', ['has', 'point_count']],
      layout: {
        'icon-image': 'location',
        'icon-size': 0.08,
        'icon-anchor': 'bottom',
        'icon-allow-overlap': true,
      },
    })
  }

  /*
   * Initialize mapbox map
   */
  useEffect(() => {
    mapboxgl.accessToken =
      'pk.eyJ1Ijoiam9uaWxpaWthbmVuIiwiYSI6ImNsZG9jajF1MTByenkzem9scWUwcHV6c2MifQ.WRzZvPuFv_fZKWN9Q7bcuQ'

    const newMap = new mapboxgl.Map(
      {
        container: mapContainer.current,
        style: 'mapbox://styles/joniliikanen/cldtzx84n002401pswass3g30',
        center: [24.948223, 60.177245],
        zoom: 9,
      },
      [],
    )

    // Add zoom and rotation controls to the map.
    newMap.addControl(new mapboxgl.NavigationControl(), 'top-right')

    setMap(newMap)

    return () => {
      newMap.remove()
    }
  }, [])

  /*
   * Add custom css for mapbox popup to hide unnecessary padding
   */
  useEffect(() => {
    // Load custom css in <script> tag with the content
    const style = document.createElement('style')
    style.innerHTML = `
      .mapboxgl-popup-content {
        padding: 0;
        font: inherit;
        border-radius: 10px;
       }
       
       .mapboxgl-popup-close-button {
        font-size: 30px;
        padding: 0 10px;
        color: white;
       }
       
       @media only screen and (max-width: 1280px) {
        .mapboxgl-popup-content {
         transform: scale(0.8);
         transform-origin: inherit;
        }
        .mapboxgl-popup-anchor-top-left {
         transform-origin: top left;
        }
        .mapboxgl-popup-anchor-top {
         transform-origin: top;
        }
        .mapboxgl-popup-anchor-top-right {
         transform-origin: top right;
        }
        .mapboxgl-popup-anchor-bottom-left {
         transform-origin: bottom left;
        }
        .mapboxgl-popup-anchor-bottom {
         transform-origin: bottom;
        }
        .mapboxgl-popup-anchor-bottom-right {
         transform-origin: bottom right;
        }
       }`
    document.head.appendChild(style)

    // Disable default scroll zoom
    window.addEventListener(
      'wheel',
      (event) => {
        if (event.ctrlKey) {
          event.preventDefault()
        }
      },
      { passive: false },
    )
  }, [])

  /*
   * Handle adding event markers to the map when the map is loaded and when events change
   */
  useEffect(() => {
    if (!map || !map.isStyleLoaded()) return

    // Define a function to clear existing layers and sources
    const clearData = () => {
      if (map.getLayer('clusters')) map.removeLayer('clusters')
      if (map.getLayer('cluster-count')) map.removeLayer('cluster-count')
      if (map.getLayer('unclustered-point'))
        map.removeLayer('unclustered-point')
      if (map.getSource('fetchedEvents')) map.removeSource('fetchedEvents')
    }

    const handleEventChange = () => {
      // Clear existing layers and source
      clearData()

      // Re-add new source and layers
      const eventDataSource = {
        type: 'FeatureCollection',
        features: events
          .map((event) => {
            const lngLat = extractLngLatFromGMapsLink(event.map_link)
            if (!lngLat) return
            return {
              type: 'Feature',
              properties: {
                ...event,
                lngLat,
              },
              geometry: {
                type: 'Point',
                coordinates: [lngLat.lng, lngLat.lat],
              },
            }
          })
          .filter((event) => event),
      }

      addLayersAndSources(eventDataSource)
    }

    // Run when events change
    handleEventChange()

    // Clean up event listener on component unmount
    return () => {
      map.off('sourcedata', handleEventChange)
    }
  }, [map, events])

  /*
   * Add event markers to the map and make sure that the map is centered
   */
  useEffect(() => {
    if (!map) return

    map.scrollZoom.disable()

    map.on('mousedown', () => {
      setDisplayZoomInfo(false)
    })

    const handleKeyDown = (e) => {
      if (e.key === 'Control') {
        map.scrollZoom.enable()
      }
    }

    const handleKeyUp = (e) => {
      if (e.key === 'Control') {
        map.scrollZoom.disable()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    window.addEventListener('keyup', handleKeyUp)

    const eventCoordinates = []

    const eventDataSource = {
      type: 'FeatureCollection',
      features: events
        .map((event) => {
          const lngLat = extractLngLatFromGMapsLink(event.map_link)
          if (!lngLat) return
          return {
            type: 'Feature',
            properties: {
              ...event,
              lngLat,
            },
            geometry: {
              type: 'Point',
              coordinates: [lngLat.lng, lngLat.lat],
            },
          }
        })
        .filter((event) => event),
    }

    events.forEach((event) => {
      if (!event.map_link || !extractLngLatFromGMapsLink(event.map_link)) return

      const { lat, lng } = extractLngLatFromGMapsLink(event.map_link)

      if (lat && lng) {
        eventCoordinates.push({ lat, lng })
      }
    })

    map.on('load', () => {
      const filenameAppendix = window.location.href.split(
        window.location.pathname,
      )[0]

      map.loadImage(
        `${filenameAppendix}/img/location.png`,
        (locationImageError, locationImage) => {
          if (locationImageError) throw locationImageError

          map.loadImage(
            `${filenameAppendix}/img/location_selected.png`,
            (locationSelectedImageError, locationSelectedImage) => {
              if (locationSelectedImageError) throw locationSelectedImageError

              map.addImage('location', locationImage)
              map.addImage('locationSelected', locationSelectedImage)

              addLayersAndSources(eventDataSource)
            },
          )
        },
      )

      map.on('click', 'clusters', (e) => {
        const features = map.queryRenderedFeatures(e.point, {
          layers: ['clusters'],
        })
        const clusterId = features[0].properties.cluster_id
        map
          .getSource('fetchedEvents')
          .getClusterExpansionZoom(clusterId, (err, expansionZoom) => {
            if (err) return

            map.easeTo({
              center: features[0].geometry.coordinates,
              zoom: expansionZoom,
            })
          })
      })

      map.on('click', 'unclustered-point', async (e) => {
        if (!e.features?.length) return

        if (selectedId) {
          map.removeFeatureState({ source: 'fetchedEvents', id: selectedId })
        }

        const { id } = e.features[0]
        setSelectedId(id)

        map.setFeatureState({ source: 'fetchedEvents', id }, { selected: true })

        const event = e.features[0].properties

        event.image = JSON.parse(event.image)

        const placeholder = document.createElement('div')
        ReactDOM.render(<EventCard event={event} />, placeholder)

        const coordinates = extractLngLatFromGMapsLink(event.map_link)

        const popup = new mapboxgl.Popup()
          .setDOMContent(placeholder)
          .setLngLat(coordinates)
          .addTo(map)
          .setMaxWidth('300px')

        // Convert the target point to pixel space
        const targetPixel = map.project(coordinates)

        // Adjust the target pixel position
        // eslint-disable-next-line no-underscore-dangle
        targetPixel.y += popup._container.offsetHeight / 2

        // Move the map to the adjusted position
        map.flyTo({
          center: map.unproject(targetPixel),
          speed: 0.5, // make the flying slow
          curve: 1, // change the speed at which it zooms out
        })
      })
    })

    if (!center || !zoom) {
      if (eventCoordinates.length > 0) {
        const bounds = new mapboxgl.LngLatBounds()
        eventCoordinates.forEach((coordinate) => {
          bounds.extend(coordinate)
        })
        map.fitBounds(bounds, { padding: 50 })
      }
    } else {
      map.setCenter(center)
      map.setZoom(zoom)
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
      window.removeEventListener('keyup', handleKeyUp)
    }
  }, [map, events, center, zoom])

  return (
    <div className={styles.mapParent}>
      <div ref={mapContainer} className={styles.mapContainer}></div>
      {displayZoomInfo && (
        <div className={styles.overlay}>
          Zoomataksesi karttaa paina ctrl ja skrollaa
        </div>
      )}
    </div>
  )
}

export default MapboxMap
